/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './UploadCertifiedDocumentCard.styles';
import { useIntl } from 'react-intl';
import { Button, message, Tooltip } from 'antd';
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import { UploadFileModal } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderGroupCard/UploadFileModal/UploadFileModal';
import { PaperClipOutlined } from '@ant-design/icons';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { useDraggableCard } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderDnD/useDraggableCard';
import FileProgressContext, {
  FileProgress,
} from '~src/contexts/FileProgressContext';
import { UploadFile } from 'antd/es/upload/interface';
import { v4 as uuid } from 'uuid';
import { queryCache } from 'react-query';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import moment from 'moment';
import {
  deleteVendorVoucherAttachment,
  getVendorVoucherAttachmentDownloadUrl,
} from '~src/services/api';
// Import icon renderer
import {
  renderOptionalVoucherBadge,
  renderVoucherTypeIcon,
} from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';
import Viewer from '~components/atoms/Viewers/Viewer';

export interface UploadCertifiedDocumentCardProps {
  id: string;
  type: string; // Changed to string to match DnD type from ContractBuilder
  subType: number; // Actual voucher type from subType prop
  cardTitle?: string;
  cardSubtitle?: string;
  cardQuantity?: number;
  cardPrice?: number;
  cardSum?: number;

  // Project side attachments – kept for compatibility, but not used here
  cardFiles: FileCardType[];

  // Vendor side attachments – from voucher_responses for this voucher
  vendorFiles?: FileCardType[];

  borderless?: boolean;
  contractData: any;
  onContextMenu?: (e: React.MouseEvent) => void;
  onCardAction?: (action: string) => void;
  groupIndex?: number;
  onRightClick?: (e: React.MouseEvent) => void;
  setDraggingCardId?: (id: string | null) => void;
  isDragging?: boolean;
  isOnMyTender?: boolean;
  tenderClosed?: boolean;
  isOfferLocked?: boolean;
  cardCreationDate: number;

  responseData?: {
    voucher_id: number;
    sum: number | null;
    is_approved: boolean | null;
  } | null;

  contractDataMyTender: {
    shareId: string;
    shareKey: string;
    contractOfferData: {
      is_offer_created: boolean;
      offer_id: number;
      state: number;
      contract_id: number;
    };
  };

  isDeleted?: boolean;
  contractDeleted?: boolean;
  isOptional?: boolean;
}

type FileCardType = {
  id: string;
  name: string;
  description: string;
  file: File;
  size: number;
  serial_number: number;
  version: number;
  version_at: number;
  type?: string;
};

const UploadCertifiedDocumentCard: React.FC<UploadCertifiedDocumentCardProps> = ({
  id,
  subType, // Added subType destructuring
  cardTitle,
  cardSubtitle,
  borderless,
  cardFiles,
  vendorFiles,
  contractData,
  onContextMenu,
  onCardAction,
  onRightClick,
  setDraggingCardId,
  isDragging,
  isOnMyTender,
  tenderClosed,
  isOfferLocked,
  cardCreationDate,
  contractDataMyTender,
  isDeleted,
  contractDeleted,
  isOptional,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // contractData stays for DnD / future usage
  // const { projectId, tenderId, contractId } = contractData;

  const [showUploadModal, setShowUploadModal] = useState(false);

  // We ignore project cardFiles in this card – only vendor answers matter here
  const vendorAttachmentsRaw = (vendorFiles || []) as FileCardType[];

  // Only latest vendor attachment by serial_number
  const latestVendorAttachment: FileCardType | null =
    vendorAttachmentsRaw && vendorAttachmentsRaw.length > 0
      ? vendorAttachmentsRaw.reduce((latest, current) => {
          if (!latest) return current;
          const latestSerial = latest.serial_number ?? 0;
          const currentSerial = current.serial_number ?? 0;
          return currentSerial > latestSerial ? current : latest;
        })
      : null;

  const attachments = latestVendorAttachment ? [latestVendorAttachment] : [];
  const hasAttachment = attachments.length > 0;

  const [
    showConfirmDeleteAttachmentModal,
    setShowConfirmDeleteAttachmentModal,
  ] = useState(false);
  const [
    attachmentToDelete,
    setAttachmentToDelete,
  ] = useState<FileCardType | null>(null);

  // Drawer state for file preview
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    extension: string;
    dbFile?: FileCardType;
  } | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const { setFileProgressContext, fileProgressContext } = useContext(
    FileProgressContext
  );

  // Vendor can upload only when offer is created
  const canUpload =
    !!isOnMyTender &&
    !!contractDataMyTender?.contractOfferData?.is_offer_created;

  // Extract primitives to avoid unstable dependency issues in useEffect
  const shareId = contractDataMyTender?.shareId;
  const shareKey = contractDataMyTender?.shareKey;
  const offerId = contractDataMyTender?.contractOfferData?.offer_id;

  // Track upload status to prevent infinite invalidation loops
  const wasUploading = useRef(false);

  // Refetch voucher_responses after vendor uploads are finished
  useEffect(() => {
    if (!isOnMyTender || !id) return;
    if (!shareId || !shareKey || !offerId) return;

    const hasPendingVendorUploads = fileProgressContext?.some(
      (f) =>
        f.isVendorVoucherResponse &&
        f.voucherId === id &&
        f.shareId === shareId &&
        f.shareKey === shareKey &&
        f.offerId === offerId &&
        !f.isDone
    );

    // If we have pending uploads, mark state as 'wasUploading'
    if (hasPendingVendorUploads) {
      wasUploading.current = true;
    }

    // Only invalidate if we WERE uploading and now we are NOT (finished)
    if (!hasPendingVendorUploads && wasUploading.current) {
      wasUploading.current = false; // Reset state
      queryCache.invalidateQueries([
        'voucher-responses',
        shareId,
        shareKey,
        offerId,
      ]);
      queryCache.invalidateQueries([
        'contracts',
        undefined,
        undefined,
        shareId,
        shareKey,
      ]);
    }
    // Dependency array now uses primitives (shareId, shareKey, offerId) instead of the unstable object
  }, [fileProgressContext, isOnMyTender, id, shareId, shareKey, offerId]);

  // Upload file (Vendor side only, via TUS)
  const handleConfirmUpload = (fileList: UploadFile[]) => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;

    if (isOnMyTender) {
      if (!shareId || !shareKey || !offerId) {
        message.error(
          intl.formatMessage({ id: 'missing-share-offer-data-upload' })
        );
        return;
      }

      setFileProgressContext((prev) => [
        ...(prev || []),
        ...fileList.map(
          (file) =>
            // @ts-ignore
            ({
              id: uuid(),
              file: file.originFileObj!,
              shouldStartUpload: true,
              isDone: false,
              voucherId: id,
              entityCreated: false,
              commit: undefined,
              progress: 0,
              // Vendor flow
              shareId,
              shareKey,
              offerId,
              isVendorVoucherResponse: true,
            } as FileProgress)
        ),
      ]);

      setShowUploadModal(false);
      return;
    }

    // Project side does not upload any files in this card
    setShowUploadModal(false);
  };

  // Confirm Delete Attachment Modal
  const handleConfirmDeleteAttachment = async () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    if (!attachmentToDelete) return;

    try {
      if (isOnMyTender) {
        if (!shareId || !shareKey || !offerId) {
          message.error(
            intl.formatMessage({ id: 'missing-share-offer-data-delete' })
          );
          return;
        }

        await deleteVendorVoucherAttachment({
          shareId,
          shareKey,
          offerId,
          voucherId: id,
          fileGuid: attachmentToDelete.id,
        });

        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );

        queryCache.invalidateQueries([
          'voucher-responses',
          shareId,
          shareKey,
          offerId,
        ]);
        queryCache.invalidateQueries([
          'contracts',
          undefined,
          undefined,
          shareId,
          shareKey,
        ]);
      }
    } finally {
      setAttachmentToDelete(null);
      setShowConfirmDeleteAttachmentModal(false);
    }
  };

  const handleOpenUploadModal = () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    setShowUploadModal(true);
  };

  // Project context menu – only card actions (no file upload)
  const handleContextMenuClick = (item: DataContextMenuItemType) => {
    if (tenderClosed || isOfferLocked || contractDeleted) return;

    switch (item.modal) {
      case 'delete':
        onCardAction?.('delete');
        break;
      case 'reactivate-card':
        onCardAction?.('reactivate-card');
        break;
      case 'edit-card':
        onCardAction?.('edit-card');
        break;
      case 'add-file':
        // No project uploads in this card
        break;
    }
  };

  // DnD
  const cardRef = useRef<HTMLDivElement>(null);
  useDraggableCard(cardRef, id, {
    disabled:
      isOnMyTender ||
      tenderClosed ||
      isOfferLocked ||
      !!isDeleted ||
      !!contractDeleted,
    onDragStart: () => {
      if (!isOnMyTender) setDraggingCardId?.(id);
    },
    onDrop: () => {
      if (!isOnMyTender) setDraggingCardId?.(null);
    },
  });

  // ToolTip Text
  const dragDropTooltipTitle =
    !isOnMyTender &&
    intl.formatMessage({
      id: 'drag-and-drop-to-reorder',
    });

  const RightClickWrapper = !isOnMyTender ? BaseRightClickOverlay : Fragment;
  const wrapperProps = !isOnMyTender
    ? {
        items: (isDeleted
          ? dataContextMenu.deletedContractCard
          : dataContextMenu.contractCard
        ).map((item) => ({
          ...item,
          disabled:
            !isOnMyTender && (tenderClosed || isOfferLocked || contractDeleted)
              ? true
              : item.disabled,
        })),
        onItemClick: handleContextMenuClick,
        onOverlayClose: () => {
          //
        },
      }
    : {};

  // Keep extension visible when truncating
  const formatFilename = (name: string, maxLength: number) => {
    const lastDot = name.lastIndexOf('.');

    if (lastDot === -1) {
      // no extension
      if (name.length <= maxLength) return name;
      return name.slice(0, maxLength) + '…';
    }

    const base = name.slice(0, lastDot);
    const ext = name.slice(lastDot);
    if (name.length <= maxLength) return name;

    // Leave extension visible, shorten only the base
    const shortenedBase = base.slice(0, maxLength - ext.length - 1);

    return shortenedBase + '…' + ext;
  };

  // Helper functions for drawer preview
  const isPreviewable = (extension: string) => {
    const ext = extension.toLowerCase();
    const isPdf = /^pdf$/i.test(ext);
    const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
    return isPdf || isImage;
  };

  const handleDrawerClose = () => {
    setPreviewDrawerVisible(false);
    setPreviewFile(null);
  };

  const handleFileDownload = () => {
    if (previewFile?.url) {
      const link = document.createElement('a');
      link.href = previewFile.url;
      link.download = previewFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAttachmentPreview = async (file: FileCardType) => {
    const extension = file.name.split('.').pop() || '';
    if (!isPreviewable(extension)) return;

    // Open drawer immediately with empty URL (shows loading spinner)
    setPreviewFile({ url: '', name: file.name, extension, dbFile: file });
    setPreviewDrawerVisible(true);
    setIsLoadingUrl(true);

    try {
      if (!shareId || !shareKey || !offerId) {
        message.error(
          intl.formatMessage({
            id: 'missing-share-offer-data-download',
          })
        );
        setPreviewDrawerVisible(false);
        setPreviewFile(null);
        return;
      }

      const url = await getVendorVoucherAttachmentDownloadUrl({
        shareId,
        shareKey,
        offerId,
        voucherId: id,
        fileGuid: file.id,
      });

      // Update with actual URL - Viewer will re-render with content
      setPreviewFile({ url, name: file.name, extension, dbFile: file });
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'attachment-download-failed',
        })
      );
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const renderAttachmentRow = (file: FileCardType) => {
    const maxNameLength = 26;
    const displayName = formatFilename(file.name, maxNameLength);

    const lowerName = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');
    const isXls =
      file.type === 'application/xlsx' ||
      file.type === 'application/xls' ||
      lowerName.endsWith('.xlsx') ||
      lowerName.endsWith('.xls');

    return (
      <div
        key={file.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: 'flex-end',
          padding: '5px 15px 5px 5px',
          whiteSpace: 'nowrap',
        }}>
        {isPdf ? (
          <IconButton
            icon={'file-pdf'}
            iconStyle={'fad'}
            color={'red'}
            backgroundOff
            customStyle={{ fontSize: '18px' }}
            containerStyle={{
              maxHeight: '18px',
              maxWidth: '18px',
              minHeight: '18px',
              minWidth: '18px',
            }}
          />
        ) : isXls ? (
          <IconButton
            icon={'file-spreadsheet'}
            iconStyle={'fad'}
            color={'green'}
            backgroundOff
            customStyle={{ fontSize: '18px' }}
            containerStyle={{
              maxHeight: '18px',
              maxWidth: '18px',
              minHeight: '18px',
              minWidth: '18px',
            }}
          />
        ) : (
          // @ts-ignore
          <PaperClipOutlined style={{ fontSize: '18px' }} />
        )}

        <span
          style={{
            fontWeight: 'lighter',
            fontSize: '14px',
            whiteSpace: 'nowrap',
          }}>
          {displayName}
        </span>

        <div>
          {/* Preview icon for previewable files */}
          {isPreviewable(file.name.split('.').pop() || '') && (
            <IconButton
              icon={'eye'}
              iconStyle={'far'}
              color={'red'}
              customStyle={{ fontSize: '16px' }}
              click={() => handleAttachmentPreview(file)}
            />
          )}

          {/* Download – vendor side */}
          <IconButton
            icon={'download'}
            iconStyle={'far'}
            color={'blue'}
            click={async () => {
              // if (tenderClosed) return; // TV1-513

              try {
                if (!isOnMyTender) return;

                if (!shareId || !shareKey || !offerId) {
                  message.error(
                    intl.formatMessage({
                      id: 'missing-share-offer-data-download',
                    })
                  );
                  return;
                }

                const url = await getVendorVoucherAttachmentDownloadUrl({
                  shareId,
                  shareKey,
                  offerId,
                  voucherId: id,
                  fileGuid: file.id,
                });

                window.open(url, '_blank');
              } catch (err) {
                message.error(
                  intl.formatMessage({
                    id: 'attachment-download-failed',
                  })
                );
              }
            }}
            customStyle={{ fontSize: '16px' }}
          />

          {/* Delete – vendor only */}
          {isOnMyTender && (
            <IconButton
              icon={'trash'}
              iconStyle={'far'}
              color={
                tenderClosed || isOfferLocked || isDeleted || contractDeleted
                  ? 'grey'
                  : 'red'
              }
              click={() => {
                if (
                  tenderClosed ||
                  isOfferLocked ||
                  isDeleted ||
                  contractDeleted
                )
                  return;
                setAttachmentToDelete(file);
                setShowConfirmDeleteAttachmentModal(true);
              }}
              customStyle={{
                fontSize: '16px',
                cursor:
                  tenderClosed || isOfferLocked || isDeleted || contractDeleted
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  tenderClosed || isOfferLocked || isDeleted || contractDeleted
                    ? 0.5
                    : 1,
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <RightClickWrapper {...wrapperProps}>
        <div
          ref={cardRef}
          css={[
            styles.cardWrapper,
            borderless && { border: 'none' },
            isDragging && styles.cardGhost,
            isDeleted && {
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              opacity: 0.88,
            },
          ]}
          onContextMenu={onContextMenu}>
          {/* HEADER */}
          {cardTitle &&
            (isOnMyTender ? (
              <div
                css={{
                  display: 'flex',
                  flex: '4',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <div css={{ width: '100%' }}>
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}>
                    {isOptional && renderOptionalVoucherBadge(intl)}

                    <div css={[styles.cardTitle, { fontSize: '16px' }]}>
                      {cardTitle}
                    </div>
                  </div>
                  {cardSubtitle && (
                    <div
                      css={{
                        marginTop: 4,
                        color: 'gray',
                        fontSize: '14px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                      {cardSubtitle}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div css={styles.cardTitleWrapper}>
                {/* Wrap Title and Icon in flex container */}
                <div
                  css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isOptional && renderOptionalVoucherBadge(intl)}

                  <div css={{ display: 'table', width: 'auto' }}>
                    <Tooltip
                      title={dragDropTooltipTitle}
                      placement="top"
                      mouseEnterDelay={0.5}
                      trigger={['hover']}>
                      <div
                        css={[
                          styles.cardTitle,
                          !cardSubtitle && { fontSize: '20px' },
                          { display: 'inline-block', width: 'auto' },
                        ]}>
                        {cardTitle}
                      </div>
                    </Tooltip>
                  </div>
                  {/* Voucher type icon for project side */}
                  {renderVoucherTypeIcon(subType, intl)}
                </div>
                <div
                  css={styles.cardSubTitle}
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                  {cardSubtitle}
                </div>
              </div>
            ))}

          {/* VENDOR FILES – only on MyTender */}
          {isOnMyTender && (
            <div
              css={{
                flex: 1,
                paddingLeft: 8,
                textAlign: 'right',
              }}>
              <div
                css={{
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  marginBottom: 4,
                  color: 'rgba(0,0,0,0.45)',
                  padding: attachments.length === 0 ? '0' : '0px 15px 0px 0px',
                }}>
                {intl.formatMessage({ id: 'vendor-file' })}
              </div>

              {attachments.length === 0 && (
                <span
                  style={{
                    color: 'grey',
                    fontSize: '14px',
                    fontWeight: 'lighter',
                    display: 'block',
                    whiteSpace: 'nowrap',
                  }}>
                  {intl.formatMessage({ id: 'no-attachment-available' })}
                </span>
              )}

              {attachments.map((file) => renderAttachmentRow(file))}
            </div>
          )}

          {/* Upload Button – vendor + created offer */}
          {isOnMyTender && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              {canUpload && (
                <Button
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={handleOpenUploadModal}
                  disabled={
                    hasAttachment ||
                    !!tenderClosed ||
                    !!isOfferLocked ||
                    !!isDeleted ||
                    !!contractDeleted
                  }>
                  <IconButton
                    icon={'up-from-bracket'}
                    iconStyle={'far'}
                    backgroundOff
                    customStyle={{ fontSize: '16px' }}
                  />
                  <span>{intl.formatMessage({ id: 'upload-str' })}</span>
                </Button>
              )}
            </div>
          )}

          {/* Vendor type + date */}
          {isOnMyTender && (
            <div
              css={{
                textAlign: 'right',
                color: 'gray',
                fontSize: '12px',
              }}>
              <div>
                {/* {moment.unix(cardCreationDate).format('DD/MM/YYYY - HH:mm')} TV1-511 */}
              </div>
              <div style={{ marginTop: '4px' }}>
                {renderVoucherTypeIcon(subType, intl)}
              </div>
            </div>
          )}

          {/* Project kebab – only card actions (no files) */}
          {!isOnMyTender && (
            <div css={styles.cardOptionWrapper}>
              <IconButton
                icon={['far', 'ellipsis-v']}
                widthTooltip={intl.formatMessage({ id: 'settings' })}
                customStyle={{
                  cursor:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 0.5
                      : 1,
                }}
                click={(e) => {
                  onRightClick?.(e);
                  const fakeEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(fakeEvent);
                }}
              />
            </div>
          )}
        </div>
      </RightClickWrapper>

      {/* MODALS */}
      <UploadFileModal
        maxFiles={1}
        visible={showUploadModal}
        beforeUpload={() => false}
        action=""
        onCancel={() => {
          setShowUploadModal(false);
        }}
        onConfirm={handleConfirmUpload}
      />
      <SimpleConfirmModal
        visible={showConfirmDeleteAttachmentModal}
        isLoading={false}
        okButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        onOk={handleConfirmDeleteAttachment}
        onCancel={() => {
          setAttachmentToDelete(null);
          setShowConfirmDeleteAttachmentModal(false);
        }}
        modalTitle={`${intl.formatMessage({ id: 'delete-attachment' })} - ${
          attachmentToDelete?.name
        }`}
        modalIcon={['far', 'trash']}
        okText={intl.formatMessage({ id: 'delete' })}
        okType="danger"
      />

      {/* Drawer preview */}
      {previewFile && (
        <Viewer
          mode="drawer"
          drawerVisible={previewDrawerVisible}
          onDrawerClose={handleDrawerClose}
          drawerTitle={previewFile.name}
          drawerWidth={800}
          fileUrl={previewFile.url}
          fileExtension={previewFile.extension}
          showDownloadButton={true}
          onDownload={handleFileDownload}
          isLoadingUrl={isLoadingUrl}
        />
      )}
    </Fragment>
  );
};

export default UploadCertifiedDocumentCard;
